<template>
  <div class="full-course-builder">
    <Sidebar />
    <div class="content-wrapper">
      <h1 class="page-title">Full Course Builder</h1>
      
      <div class="course-form-section">
        <h2>Course Information</h2>
        <div class="form-group">
          <label for="title">Course Title</label>
          <input id="title" v-model="course.title" type="text" class="form-control" placeholder="Enter course title" />
        </div>
        
        <div class="form-group">
          <label for="description">Course Description</label>
          <QuillEditor
            id="description"
            v-model:content="course.description"
            label="Course Description"
            :toolbar="toolbarOptions"
            placeholder="Enter course description"
          />
        </div>
        
        <div class="form-row">
          <div class="form-group">
            <label for="instructor">Instructor</label>
            <select id="instructor" v-model="course.instructor_id" class="form-control">
              <option value="" disabled>Select instructor</option>
              <option v-for="instructor in instructors" :key="instructor.id" :value="instructor.id">
                {{ instructor.first_name }} {{ instructor.last_name }}
              </option>
            </select>
          </div>
          
          <div class="form-group">
            <label for="category">Category</label>
            <select id="category" v-model="course.category_id" class="form-control">
              <option value="" disabled>Select category</option>
              <option v-for="category in categories" :key="category.id" :value="category.id">
                {{ category.title }}
              </option>
            </select>
          </div>
        </div>
        
        <div class="form-row">
          <div class="form-group">
            <label for="price">Price</label>
            <input id="price" v-model.number="course.price" type="number" step="0.01" class="form-control" placeholder="Enter price" :disabled="course.is_premium" />
            <small v-if="course.is_premium" class="text-muted">Price is not applicable for premium courses</small>
          </div>
          
          <div class="form-group">
            <label for="status">Status</label>
            <select id="status" v-model="course.status" class="form-control">
              <option value="active">Active</option>
              <option value="draft">Draft</option>
              <option value="archived">Archived</option>
            </select>
          </div>
        </div>
        
        <div class="form-row">
          <div class="form-group">
            <label for="title_ar">Arabic Title</label>
            <input id="title_ar" v-model="course.title_ar" type="text" class="form-control" placeholder="Enter Arabic title" />
          </div>
          
          <div class="form-group">
            <label>Premium Course</label>
            <div class="toggle-switch">
              <input type="checkbox" id="premium" v-model="course.is_premium" />
              <label for="premium"></label>
            </div>
          </div>
        </div>
        
        <div class="form-group">
          <label for="description_ar">Arabic Description</label>
          <QuillEditor
            id="description_ar"
            v-model:content="course.description_ar"
            label="Arabic Description"
            :toolbar="toolbarOptions"
            placeholder="Enter Arabic course description"
          />
        </div>
      </div>
      
      <div class="course-form-section">
        <h2>Course Media</h2>
        
        <div class="form-group">
          <label for="image_url">Course Image URL</label>
          <div class="input-with-button">
            <input id="image_url" v-model="course.urls.image_url" type="text" class="form-control" placeholder="Enter image URL" />
            <button type="button" class="btn btn-secondary" @click="openImageUploader('image')">
              Upload Image
            </button>
          </div>
        </div>
        
        <div class="form-group">
          <label for="downloadable_url">Downloadable Content URL</label>
          <div class="input-with-button">
            <input id="downloadable_url" v-model="course.urls.downloadable_url" type="text" class="form-control" placeholder="Enter downloadable content URL" />
            <button type="button" class="btn btn-secondary" @click="openImageUploader('downloadable')">
              Upload File
            </button>
          </div>
        </div>
        
        <div class="form-group">
          <label for="preview_video_id">Preview Video</label>
          <select id="preview_video_id" v-model="course.urls.preview_video_id" class="form-control">
            <option value="" disabled>Select preview video</option>
            <option v-for="video in availableVideos" :key="video.video_id" :value="video.video_id">
              {{ video.title }}
            </option>
          </select>
        </div>
        
        <div class="form-group" v-if="previewVideo">
          <label>Preview Video Details</label>
          <div class="video-preview">
            <div class="video-thumbnail">
              <img :src="previewVideo.thumbnail_url" alt="Video thumbnail" />
            </div>
            <div class="video-info">
              <p><strong>Title:</strong> {{ previewVideo.title }}</p>
              <p><strong>URL:</strong> {{ previewVideo.mp4_url }}</p>
              <p><strong>Thumbnail:</strong> {{ previewVideo.thumbnail_url }}</p>
            </div>
          </div>
          <div class="form-actions-preview">
            <button type="button" class="btn btn-secondary" @click="autoFillPreviewUrls">
              Auto-fill preview URLs
            </button>
            <button type="button" class="btn btn-secondary" @click="openImageUploader('preview_thumbnail')">
              Upload Custom Thumbnail
            </button>
          </div>
        </div>
      </div>
      
      <div class="course-form-section">
        <h2>Course Lessons</h2>
        
        <div v-if="loading" class="loading-spinner">
          <div class="spinner"></div>
          <p>Loading videos...</p>
        </div>
        
        <div v-else-if="error" class="error-message">
          <p>{{ error }}</p>
          <button @click="fetchAvailableVideos" class="btn btn-primary">Retry</button>
        </div>
        
        <div v-else>
          <div class="video-filter">
            <input 
              v-model="searchTerm" 
              type="text" 
              placeholder="Search videos..." 
              class="form-control search-input" 
            />
            
            <div class="selected-count">
              {{ selectedVideos.length }} videos selected
            </div>
          </div>
          
          <div class="videos-container">
            <div 
              v-for="video in filteredVideos" 
              :key="video.video_id"
              class="video-card"
              :class="{ 'selected': isVideoSelected(video.video_id) }"
              @click="toggleVideoSelection(video)"
            >
              <div class="video-thumbnail">
                <img :src="video.thumbnail_url" alt="Video thumbnail" />
                <div class="video-duration" v-if="video.duration">
                  {{ formatDuration(video.duration) }}
                </div>
                <div class="selection-indicator">
                  <i class="fas" :class="isVideoSelected(video.video_id) ? 'fa-check-circle' : 'fa-circle'"></i>
                </div>
              </div>
              <div class="video-details">
                <h3 class="video-title">{{ video.title }}</h3>
                <p class="video-creation-date">{{ formatDate(video.created_at) }}</p>
              </div>
            </div>
          </div>
          
          <div v-if="selectedVideos.length > 0" class="selected-videos-section">
            <h3>Selected Videos ({{ selectedVideos.length }})</h3>
            <p class="help-text">Drag and drop to reorder the lessons</p>
            
            <draggable 
              v-model="selectedVideos" 
              item-key="video_id"
              class="selected-videos-list"
              handle=".drag-handle"
            >
              <template #item="{ element, index }">
                <div class="selected-video-item">
                  <div class="drag-handle">
                    <i class="fas fa-grip-lines"></i>
                  </div>
                  <div class="video-number">{{ index + 1 }}</div>
                  <div class="video-thumbnail">
                    <img :src="element.thumbnail_url" alt="Video thumbnail" />
                  </div>
                  <div class="video-info">
                    <div class="video-title">{{ element.title }}</div>
                    <div class="video-actions">
                      <button type="button" class="btn btn-sm btn-outline-danger" @click.stop="removeVideo(element.video_id)">
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              </template>
            </draggable>
          </div>
        </div>
      </div>
      
      <div class="form-actions">
        <button type="button" class="btn btn-primary" @click="createFullCourse" :disabled="loading || creatingCourse">
          <span v-if="creatingCourse">
            <div class="spinner-sm"></div> Creating course...
          </span>
          <span v-else>Create Course</span>
        </button>
        <button type="button" class="btn btn-secondary" @click="resetForm">Reset</button>
      </div>
      
      <div v-if="creationSuccess" class="success-message">
        <p>Course created successfully!</p>
      </div>
      
      <div v-if="creationError" class="error-message">
        <p>{{ creationError }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue';
import Sidebar from './Sidebar.vue';
import draggable from 'vuedraggable';
import cloudflareR2Service from '@/services/cloudflareR2';
import { UPLOAD_CONFIG } from '@/config';
import QuillEditor from '@/components/QuillEditor.vue';

export default {
  name: 'FullCourseBuilder',
  components: {
    Sidebar,
    draggable,
    QuillEditor
  },
  setup() {
    // Course data
    const course = ref({
      title: '',
      description: '',
      instructor_id: 1,
      category_id: 1,
      price: 9.99,
      status: 'active',
      title_ar: '',
      description_ar: '',
      is_premium: true,
      urls: {
        image_url: 'https://example.com/image.jpg',
        downloadable_url: 'https://example.com/download.pdf',
        preview_video_id: '',
        preview_url: '',
        preview_thumbnail_url: ''
      },
      lessons: []
    });

    // Toolbar options for QuillEditor
    const toolbarOptions = [
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote', 'code-block'],
      [{ 'header': 1 }, { 'header': 2 }],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'indent': '-1' }, { 'indent': '+1' }],
      [{ 'direction': 'rtl' }],
      [{ 'size': ['small', false, 'large', 'huge'] }],
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'font': [] }],
      [{ 'align': [] }],
      ['clean'],
      ['link']
    ];

    // State variables
    const availableVideos = ref([]);
    const selectedVideos = ref([]);
    const searchTerm = ref('');
    const loading = ref(false);
    const error = ref(null);
    const creatingCourse = ref(false);
    const creationSuccess = ref(false);
    const creationError = ref(null);
    const categories = ref([]);
    const instructors = ref([]);

    // Computed properties
    const filteredVideos = computed(() => {
      if (!searchTerm.value) return availableVideos.value;
      
      return availableVideos.value.filter(video => 
        video.title.toLowerCase().includes(searchTerm.value.toLowerCase())
      );
    });

    const previewVideo = computed(() => {
      return availableVideos.value.find(video => video.video_id === course.value.urls.preview_video_id);
    });

    // Methods
    const fetchAvailableVideos = async () => {
      loading.value = true;
      error.value = null;
      
      try {
        const token = localStorage.getItem('token');
        
        const response = await fetch('https://api.kewaar.com/api/admin/available-videos', {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        const data = await response.json();
        
        if (data.success) {
          availableVideos.value = data.data.videos;
        } else {
          error.value = data.message || 'Failed to fetch videos';
        }
      } catch (err) {
        console.error('Error fetching videos:', err);
        error.value = 'Failed to fetch videos. Please try again.';
      } finally {
        loading.value = false;
      }
    };

    const fetchCategories = async () => {
      try {
        const token = localStorage.getItem('token');
        
        const response = await fetch('https://api.kewaar.com/api/admin/coursecategories2', {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        const data = await response.json();
        
        if (data.success) {
          categories.value = data.data.filter(category => category.is_active);
        } else {
          console.error('Failed to fetch categories:', data.message);
        }
      } catch (err) {
        console.error('Error fetching categories:', err);
      }
    };

    const fetchInstructors = async () => {
      try {
        const token = localStorage.getItem('token');
        
        const response = await fetch('https://api.kewaar.com/api/admin/instructors1', {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        const data = await response.json();
        
        if (data.success) {
          instructors.value = data.data;
        } else {
          console.error('Failed to fetch instructors:', data.message);
        }
      } catch (err) {
        console.error('Error fetching instructors:', err);
      }
    };

    const isVideoSelected = (videoId) => {
      return selectedVideos.value.some(video => video.video_id === videoId);
    };

    const toggleVideoSelection = (video) => {
      if (isVideoSelected(video.video_id)) {
        removeVideo(video.video_id);
      } else {
        selectedVideos.value.push(video);
      }
    };

    const removeVideo = (videoId) => {
      selectedVideos.value = selectedVideos.value.filter(video => video.video_id !== videoId);
    };

    const formatDate = (dateString) => {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    };

    const formatDuration = (seconds) => {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    };

    const autoFillPreviewUrls = () => {
      if (previewVideo.value) {
        course.value.urls.preview_url = previewVideo.value.mp4_url;
        course.value.urls.preview_thumbnail_url = previewVideo.value.thumbnail_url;
      }
    };

    const resetForm = () => {
      course.value = {
        title: '',
        description: '',
        instructor_id: 1,
        category_id: 1,
        price: 9.99,
        status: 'active',
        title_ar: '',
        description_ar: '',
        is_premium: true,
        urls: {
          image_url: 'https://example.com/image.jpg',
          downloadable_url: 'https://example.com/download.pdf',
          preview_video_id: '',
          preview_url: '',
          preview_thumbnail_url: ''
        },
        lessons: []
      };
      selectedVideos.value = [];
      searchTerm.value = '';
      creationSuccess.value = false;
      creationError.value = null;
    };

    const createFullCourse = async () => {
      if (selectedVideos.value.length === 0) {
        creationError.value = 'Please select at least one video for your course.';
        return;
      }
      
      creatingCourse.value = true;
      creationError.value = null;
      creationSuccess.value = false;
      
      try {
        // Prepare lessons data from selected videos
        const lessons = selectedVideos.value.map((video, index) => ({
          title: video.title,
          title_ar: `${video.title} (AR)`, // Default Arabic title
          description: video.description || `Lesson ${index + 1}`,
          description_ar: video.description ? `${video.description} (AR)` : `درس ${index + 1}`,
          lesson_video_id: video.video_id,
          lesson_url: video.mp4_url,
          lesson_thumbnail_url: video.thumbnail_url,
          duration: video.duration || 0,
          downloadable_url: video.mp4_url,
          is_hidden: false
        }));
        
        // Ensure all URLs are set properly
        const urls = {
          image_url: course.value.urls.image_url || '',
          downloadable_url: course.value.urls.downloadable_url || '',
          preview_video_id: course.value.urls.preview_video_id || '',
          preview_url: course.value.urls.preview_url || '',
          preview_thumbnail_url: course.value.urls.preview_thumbnail_url || ''
        };
        
        // Create the complete course payload
        const coursePayload = {
          ...course.value,
          urls,
          lessons
        };
        
        // Make the API request
        const token = localStorage.getItem('token');
        
        const response = await fetch('https://api.kewaar.com/api/admin/courses/complete', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(coursePayload)
        });
        
        const data = await response.json();
        
        if (data.success) {
          creationSuccess.value = true;
          // Optionally reset the form after successful creation
          resetForm();
        } else {
          creationError.value = data.message || 'Failed to create course';
        }
      } catch (err) {
        console.error('Error creating course:', err);
        creationError.value = 'Failed to create course. Please try again.';
      } finally {
        creatingCourse.value = false;
      }
    };

    // File upload methods
    const openImageUploader = async (field) => {
      // Create a file input element programmatically
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = field === 'image' 
        ? UPLOAD_CONFIG.ALLOWED_IMAGE_TYPES.join(',')
        : [...UPLOAD_CONFIG.ALLOWED_DOCUMENT_TYPES, ...UPLOAD_CONFIG.ALLOWED_VIDEO_TYPES].join(',')
      
      input.onchange = async (event) => {
        const file = event.target.files[0]
        if (!file) return
        
        try {
          const uploadResponse = await cloudflareR2Service.uploadWithValidation(file)
          const fileUrl = uploadResponse.fileUrl
          
          // Update the corresponding field
          if (field === 'image') {
            course.value.urls.image_url = fileUrl
          } else if (field === 'downloadable') {
            course.value.urls.downloadable_url = fileUrl
          } else if (field === 'preview_thumbnail') {
            course.value.urls.preview_thumbnail_url = fileUrl
          }
        } catch (error) {
          console.error('R2 upload error:', error)
        }
      }
      
      input.click()
    };

    // Lifecycle hooks
    onMounted(() => {
      fetchAvailableVideos();
      fetchCategories();
      fetchInstructors();
    });
    
    // Watch for preview video change to auto-fill URLs
    watch(() => course.value.urls.preview_video_id, (newVal) => {
      if (newVal && previewVideo.value) {
        course.value.urls.preview_url = previewVideo.value.mp4_url;
        course.value.urls.preview_thumbnail_url = previewVideo.value.thumbnail_url;
      }
    });
    
    // Watch for premium status change
    watch(() => course.value.is_premium, (isPremium) => {
      if (isPremium) {
        // Store the current price before setting it to null
        course.value._previousPrice = course.value.price;
        course.value.price = null;
      } else if (course.value._previousPrice) {
        // Restore the previous price when switching back to non-premium
        course.value.price = course.value._previousPrice;
        delete course.value._previousPrice;
      } else {
        // Default price if there was no previous price
        course.value.price = 9.99;
      }
    });

    return {
      course,
      availableVideos,
      selectedVideos,
      searchTerm,
      loading,
      error,
      creatingCourse,
      creationSuccess,
      creationError,
      filteredVideos,
      previewVideo,
      categories,
      instructors,
      fetchAvailableVideos,
      fetchCategories,
      fetchInstructors,
      isVideoSelected,
      toggleVideoSelection,
      removeVideo,
      formatDate,
      formatDuration,
      autoFillPreviewUrls,
      resetForm,
      createFullCourse,
      openImageUploader,
      toolbarOptions
    };
  }
};
</script>

<style scoped>
.full-course-builder {
  display: flex;
  width: 100%;
  min-height: 100vh;
  background-color: #f7f9fc;
}

.content-wrapper {
  flex: 1;
  padding: 2rem;
  margin-left: 280px; /* Adjust based on your sidebar width */
}

.page-title {
  font-size: 2rem;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 2rem;
}

.course-form-section {
  background-color: white;
  border-radius: 0.5rem;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  margin-bottom: 2rem;
}

.course-form-section h2 {
  font-size: 1.5rem;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 1.5rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #e2e8f0;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-row {
  display: flex;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
}

.form-row .form-group {
  flex: 1;
  margin-bottom: 0;
}

label {
  display: block;
  font-weight: 500;
  margin-bottom: 0.5rem;
  color: #4a5568;
}

.form-control {
  width: 100%;
  padding: 0.75rem;
  font-size: 1rem;
  border: 1px solid #cbd5e0;
  border-radius: 0.375rem;
  background-color: #fff;
  transition: border-color 0.2s ease-in-out;
}

.form-control:focus {
  border-color: #3b82f6;
  outline: none;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.3);
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 56px;
  height: 30px;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-switch label {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #cbd5e0;
  transition: 0.4s;
  border-radius: 30px;
}

.toggle-switch label:before {
  position: absolute;
  content: "";
  height: 22px;
  width: 22px;
  left: 4px;
  bottom: 4px;
  background-color: white;
  transition: 0.4s;
  border-radius: 50%;
}

.toggle-switch input:checked + label {
  background-color: #3b82f6;
}

.toggle-switch input:checked + label:before {
  transform: translateX(26px);
}

.video-filter {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.search-input {
  flex: 1;
  max-width: 400px;
}

.selected-count {
  background-color: #3b82f6;
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 999px;
  font-weight: 500;
}

.videos-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.video-card {
  background-color: white;
  border-radius: 0.5rem;
  overflow: hidden;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.video-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.video-card.selected {
  border: 2px solid #3b82f6;
  transform: translateY(-4px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.video-thumbnail {
  position: relative;
  width: 100%;
  padding-top: 56.25%; /* 16:9 aspect ratio */
}

.video-thumbnail img {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.video-duration {
  position: absolute;
  bottom: 8px;
  right: 8px;
  background-color: rgba(0, 0, 0, 0.75);
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.75rem;
}

.selection-indicator {
  position: absolute;
  top: 8px;
  right: 8px;
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #3b82f6;
  font-size: 1rem;
}

.video-details {
  padding: 1rem;
}

.video-title {
  font-weight: 500;
  margin-bottom: 0.5rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.video-creation-date {
  font-size: 0.75rem;
  color: #718096;
  margin: 0;
}

.selected-videos-section {
  background-color: white;
  border-radius: 0.5rem;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  margin-bottom: 2rem;
}

.help-text {
  color: #718096;
  font-style: italic;
  margin-bottom: 1rem;
}

.selected-videos-list {
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  overflow: hidden;
}

.selected-video-item {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  background-color: white;
  border-bottom: 1px solid #e2e8f0;
}

.selected-video-item:last-child {
  border-bottom: none;
}

.drag-handle {
  cursor: move;
  margin-right: 0.75rem;
  color: #718096;
}

.video-number {
  font-weight: 600;
  margin-right: 1rem;
  width: 30px;
  text-align: center;
}

.selected-video-item .video-thumbnail {
  width: 100px;
  height: 56px;
  padding-top: 0;
  flex-shrink: 0;
  margin-right: 1rem;
}

.selected-video-item .video-info {
  flex: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.video-preview {
  display: flex;
  margin-top: 0.5rem;
  background-color: #f7fafc;
  border-radius: 0.5rem;
  padding: 1rem;
}

.video-preview .video-thumbnail {
  width: 160px;
  height: 90px;
  padding-top: 0;
  flex-shrink: 0;
  margin-right: 1rem;
}

.video-preview .video-info {
  flex: 1;
}

.video-preview .video-info p {
  margin-bottom: 0.5rem;
}

.loading-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 0;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 5px solid #e2e8f0;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

.spinner-sm {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid #ffffff;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 0.5rem;
  vertical-align: middle;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.error-message {
  background-color: #fed7d7;
  color: #c53030;
  padding: 1rem;
  border-radius: 0.5rem;
  margin-bottom: 2rem;
}

.success-message {
  background-color: #c6f6d5;
  color: #2f855a;
  padding: 1rem;
  border-radius: 0.5rem;
  margin-top: 2rem;
}

.form-actions {
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
}

.btn {
  padding: 0.75rem 1.5rem;
  font-weight: 500;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.btn-primary {
  background-color: #3b82f6;
  color: white;
  border: none;
}

.btn-primary:hover {
  background-color: #2563eb;
}

.btn-primary:disabled {
  background-color: #93c5fd;
  cursor: not-allowed;
}

.btn-secondary {
  background-color: white;
  color: #4a5568;
  border: 1px solid #cbd5e0;
}

.btn-secondary:hover {
  background-color: #f7fafc;
}

.btn-sm {
  padding: 0.375rem 0.75rem;
  font-size: 0.875rem;
}

.btn-outline-danger {
  color: #e53e3e;
  border: 1px solid #e53e3e;
  background-color: white;
}

.btn-outline-danger:hover {
  background-color: #e53e3e;
  color: white;
}

.input-with-button {
  display: flex;
  gap: 10px;
}

.input-with-button .form-control {
  flex: 1;
}

.input-with-button .btn {
  white-space: nowrap;
}

.form-actions-preview {
  display: flex;
  gap: 10px;
  margin-top: 10px;
}
</style> 